import { inject, Injectable, signal } from "@angular/core";
import { from, Observable } from "rxjs";
import { map, switchMap, tap } from "rxjs/operators";
import {
    AuthSession,
    ConfirmSignInOutput,
    ResetPasswordOutput,
    SignInOutput,
    signInWithRedirect
} from "aws-amplify/auth";

import { AMPLIFY_AUTH } from "./amplify-auth.token";

export type SignInStep = "DONE" | "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED";

@Injectable({
    providedIn: "root"
})
export class AuthService {
    private readonly auth = inject(AMPLIFY_AUTH);

    readonly isAuthenticated = signal(false);
    readonly isAdmin = signal(false);
    readonly isProducer = signal(false);
    readonly isHost = signal(false);
    readonly isMarketer = signal(false);

    loadSession(): Promise<void> {
        return this.auth
            .fetchAuthSession()
            .then(session => this.applySession(session))
            .catch(() => this.clearSession());
    }

    getCurrentUserEmail(): Observable<string | null> {
        return from(this.auth.fetchAuthSession()).pipe(
            map(session => {
                const payload = session.tokens?.idToken?.payload;
                const email = payload?.["email"];
                return typeof email === "string" ? email : null;
            })
        );
    }

    getCurrentUserDisplayName(): Observable<string | null> {
        return from(this.auth.fetchAuthSession()).pipe(
            map(session => {
                const payload = session.tokens?.idToken?.payload;
                const firstName = payload?.["given_name"];
                const lastName = payload?.["family_name"];
                const email = payload?.["email"];
                if (typeof firstName === "string" && typeof lastName === "string") {
                    return `${firstName} ${lastName}`;
                }
                if (typeof firstName === "string") return firstName;
                if (typeof lastName === "string") return lastName;
                return typeof email === "string" ? email.split("@")[0] : null;
            })
        );
    }

    login(email: string, password: string): Observable<SignInOutput> {
        return from(this.auth.signIn({ username: email, password })).pipe(
            switchMap(result => {
                if (result.nextStep.signInStep === "DONE") {
                    return from(this.loadSession()).pipe(map(() => result));
                }
                return from(Promise.resolve(result));
            })
        );
    }

    confirmNewPassword(newPassword: string): Observable<ConfirmSignInOutput> {
        return from(this.auth.confirmSignIn({ challengeResponse: newPassword })).pipe(
            switchMap(result => {
                if (result.nextStep.signInStep === "DONE") {
                    return from(this.loadSession()).pipe(map(() => result));
                }
                return from(Promise.resolve(result));
            })
        );
    }

    signInWithOkta(): Promise<void> {
        return signInWithRedirect({ provider: { custom: "Okta" } });
    }

    logout(): Observable<void> {
        return from(this.auth.signOut()).pipe(tap(() => this.clearSession()));
    }

    forgotPassword(email: string): Observable<ResetPasswordOutput> {
        return from(this.auth.resetPassword({ username: email }));
    }

    confirmForgotPassword(email: string, code: string, newPassword: string): Observable<void> {
        return from(
            this.auth.confirmResetPassword({
                username: email,
                confirmationCode: code,
                newPassword
            })
        );
    }

    private applySession(session: AuthSession): void {
        const groups = (session.tokens?.accessToken?.payload?.["cognito:groups"] as string[]) ?? [];
        this.isAuthenticated.set(!!session.tokens?.accessToken);
        this.isAdmin.set(groups.includes("admin"));
        this.isProducer.set(groups.includes("producer"));
        this.isHost.set(groups.includes("host"));
        this.isMarketer.set(groups.includes("marketer"));
    }

    private clearSession(): void {
        this.isAuthenticated.set(false);
        this.isAdmin.set(false);
        this.isProducer.set(false);
        this.isHost.set(false);
        this.isMarketer.set(false);
    }
}
